import { useId, type ReactNode } from "react";
import { useLocale } from "../../../context/LanguageContext";
import { Toggle } from "./Toggle";

interface ProviderCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}

export function ProviderCard({
  icon,
  title,
  description,
  enabled,
  onEnabledChange,
  disabled = false,
  children,
}: ProviderCardProps) {
  const { t } = useLocale();
  const toggleId = useId();
  const titleId = `${toggleId}-title`;

  return (
    <section
      aria-labelledby={titleId}
      className={`rounded-lg border border-border bg-card p-5 transition-opacity ${
        disabled ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-1 items-start gap-3">
          <div className="mt-0.5 shrink-0 rounded-md border border-border bg-background p-1.5 text-foreground">
            {icon}
          </div>
          <div className="flex-1">
            <h3 id={titleId} className="text-sm font-semibold text-foreground">
              {title}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            {enabled ? t("sso.provider.enabled") : t("sso.provider.disabled")}
          </span>
          <Toggle
            id={toggleId}
            checked={enabled}
            onChange={onEnabledChange}
            disabled={disabled}
            label={t("sso.provider.toggleLabel", { provider: title })}
          />
        </div>
      </div>
      {enabled && (
        <div className="mt-4 space-y-4 border-t border-border pt-4">
          {children}
        </div>
      )}
    </section>
  );
}
